import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';
import { UserPlus, Trash2, Key, Search, User, Edit2, Check, X } from 'lucide-react';
import Modal from '../components/Modal';

const Users: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<any[]>([]);
  const [search, setSearch] = useState('');
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('VISUALIZER');
  const [loading, setLoading] = useState(true);
  const [modalConfig, setModalConfig] = useState<any>({ isOpen: false, title: '', message: '', type: 'info', onConfirm: null });

  // Estados para edição em linha
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editRole, setEditRole] = useState('');

  useEffect(() => { fetchUsers(); }, [search]);

  const fetchUsers = async () => {
    try {
      const { data } = await api.get(`/users?q=${search}`);
      setUsers(data);
    } catch (err) { console.error("Erro users"); }
    finally { setLoading(false); }
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await api.post('/register', { email, password, global_role: role, full_name: fullName });
      setEmail(''); setFullName(''); setPassword(''); fetchUsers();
      setModalConfig({ isOpen: true, title: 'Sucesso', message: 'Utilizador criado.', type: 'success' });
    } catch (err) {
      setModalConfig({ isOpen: true, title: 'Erro', message: 'Falha ao criar utilizador.', type: 'danger' });
    }
  };

  const handleDeleteUser = (userId: string, userEmail: string) => {
    setModalConfig({
      isOpen: true,
      title: 'Eliminar Utilizador',
      message: `Tem a certeza que deseja eliminar permanentemente ${userEmail}?`,
      type: 'danger',
      onConfirm: async () => {
        try {
          await api.delete(`/users/${userId}`);
          setModalConfig({ isOpen: true, title: 'Sucesso', message: 'Utilizador removido.', type: 'success' });
          fetchUsers();
        } catch (err) {
          setModalConfig({ isOpen: true, title: 'Erro', message: 'Não foi possível eliminar.', type: 'danger' });
        }
      }
    });
  };

  const handleUpdateUser = async (userId: string) => {
    try {
      await api.patch(`/users/${userId}`, { full_name: editName, global_role: editRole });
      setEditingUserId(null);
      fetchUsers();
    } catch (err: any) {
      setModalConfig({ isOpen: true, title: 'Erro', message: err.response?.data?.detail || 'Falha ao atualizar o utilizador.', type: 'danger' });
    }
  };

  // Regra de autoridade para UI
  const canManage = (targetRole: string) => {
    if (currentUser?.global_role === 'SUPER_ADMIN') return true;
    if (currentUser?.global_role === 'GENERAL_ADMIN') {
        return ['PROJECT_ADMIN', 'VISUALIZER'].includes(targetRole);
    }
    return false;
  };

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      <Modal {...modalConfig} onClose={() => setModalConfig({ ...modalConfig, isOpen: false })} cancelText={modalConfig.onConfirm ? "Cancelar" : "Fechar"} />

      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 pb-8 border-b border-slate-200 dark:border-slate-800 transition-colors text-center md:text-left">
        <h2 className="text-4xl font-black text-slate-900 dark:text-white tracking-tighter uppercase">Gestão de Equipa</h2>
        <div className="relative w-full md:w-[400px]">
          <Search className="absolute left-4 top-3.5 text-slate-400" size={18} />
          <input type="text" placeholder="Pesquisar..." value={search} onChange={(e) => setSearch(e.target.value)} className="w-full pl-12 pr-4 py-3.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl outline-none focus:ring-4 focus:ring-blue-500/10 transition-all font-bold dark:text-white" />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Formulário de Criação */}
        <div className="bg-white dark:bg-slate-900 p-8 rounded-[2rem] border border-slate-200 dark:border-slate-800 h-fit transition-colors">
          <div className="flex items-center space-x-2 mb-8 text-blue-600 uppercase tracking-widest text-[10px] font-black"><UserPlus size={20} /> <span>Novo Membro</span></div>
          <form onSubmit={handleCreateUser} className="space-y-5">
            <div className="relative"><User className="absolute left-4 top-4 text-slate-300 dark:text-slate-600" size={18} /> <input type="text" value={fullName} onChange={(e) => setFullName(e.target.value)} className="w-full pl-12 pr-4 py-4 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-700 rounded-2xl outline-none focus:ring-2 focus:ring-blue-500/50 dark:text-white transition-all font-bold" placeholder="Nome..." required /></div>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full p-4 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl outline-none font-bold dark:text-white" placeholder="E-mail..." required />
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full p-4 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl outline-none font-bold dark:text-white" placeholder="Password..." required />
            <select value={role} onChange={(e) => setRole(e.target.value)} className="w-full p-4 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl outline-none font-bold dark:text-white cursor-pointer"><option value="GENERAL_ADMIN">General Admin</option><option value="PROJECT_ADMIN">Project Admin</option><option value="VISUALIZER">Visualizer</option></select>
            <button type="submit" className="w-full bg-slate-900 dark:bg-blue-600 text-white font-black py-4 rounded-2xl hover:bg-black transition-all uppercase tracking-widest text-[10px]">Criar Conta</button>
          </form>
        </div>

        {/* Tabela de Utilizadores */}
        <div className="lg:col-span-2 bg-white dark:bg-slate-900 rounded-[2rem] border border-slate-200 dark:border-slate-800 overflow-hidden transition-colors">
          <div className="overflow-x-auto">
            <table className="w-full text-left">
                <thead className="bg-slate-50/50 dark:bg-slate-950 border-b border-slate-200 dark:border-slate-700"><tr className="text-[10px] font-black text-slate-400 uppercase tracking-widest"><th className="p-6">Membro</th><th className="p-6">Autoridade</th><th className="p-6 text-right">Ações</th></tr></thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {users.map((u) => {
                    const isEditing = editingUserId === u.id;
                    const canManageUser = canManage(u.global_role);
                    return (
                    <tr key={u.id} className="hover:bg-slate-50 dark:hover:bg-blue-900/5 transition-colors">
                        <td className="p-6">
                            <div className="flex items-center space-x-4">
                                <div className="w-12 h-12 rounded-xl bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center text-blue-600 dark:text-blue-400 font-black shrink-0">{u.profile_pic_url ? <img src={u.profile_pic_url} className="w-full h-full object-cover rounded-xl" alt="P" /> : u.email[0].toUpperCase()}</div>
                                <div className="min-w-0">
                                    {isEditing ? (
                                        <input type="text" value={editName} onChange={(e) => setEditName(e.target.value)} className="bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-1 font-bold text-sm text-blue-600 outline-none w-full" />
                                    ) : (
                                        <p className="font-bold text-slate-900 dark:text-white uppercase tracking-tight truncate">{u.full_name || 'Sem Nome'}</p>
                                    )}
                                    <p className="text-[10px] text-slate-400 font-bold">{u.email}</p>
                                </div>
                            </div>
                        </td>
                        <td className="p-6">
                            {isEditing ? (
                                <select value={editRole} onChange={(e) => setEditRole(e.target.value)} className="text-[10px] font-black bg-slate-50 dark:bg-slate-800 p-1 rounded-lg border border-slate-200 dark:border-slate-700">
                                    <option value="GENERAL_ADMIN">GENERAL_ADMIN</option>
                                    <option value="PROJECT_ADMIN">PROJECT_ADMIN</option>
                                    <option value="VISUALIZER">VISUALIZER</option>
                                </select>
                            ) : (
                                <span className={`text-[10px] px-2 py-1 rounded-full font-black tracking-widest ${u.global_role === 'SUPER_ADMIN' ? 'bg-red-50 text-red-600' : 'bg-slate-100 dark:bg-slate-800 text-slate-500'}`}>{u.global_role}</span>
                            )}
                        </td>
                        <td className="p-6 text-right">
                            <div className="flex justify-end space-x-2">
                                {canManageUser && u.id !== currentUser?.id && (
                                    <>
                                        {isEditing ? (
                                            <>
                                                <button onClick={() => handleUpdateUser(u.id)} className="p-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-all"><Check size={14}/></button>
                                                <button onClick={() => setEditingUserId(null)} className="p-2 bg-slate-200 text-slate-600 rounded-lg hover:bg-slate-300 transition-all"><X size={14}/></button>
                                            </>
                                        ) : (
                                            <>
                                                <button onClick={() => { setEditingUserId(u.id); setEditName(u.full_name || ''); setEditRole(u.global_role); }} className="p-2 text-slate-400 hover:text-blue-600 transition-all" title="Editar"><Edit2 size={16} /></button>
                                                <button onClick={() => handleDeleteUser(u.id, u.email)} className="p-2 text-slate-400 hover:text-red-500 transition-all" title="Eliminar"><Trash2 size={16} /></button>
                                            </>
                                        )}
                                    </>
                                )}
                            </div>
                        </td>
                    </tr>
                )})}
                </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Users;
